import {
  COSMOS_DEFAULT_FOOTPRINT_RADIUS,
  distributeConstellationAnchors,
} from "@chuzi/shared/realms/cosmos/components";
import type { StoryListItem } from "@chuzi/shared/types";
import {
  buildTagNebulaLayout,
  type TagNebulaCluster,
  type TagNebulaLayout,
} from "./tag-nebula-layout";

export const CHUZI_CLUSTER_TAG = "__chuzi__";
export const CHUZI_NEBULA_COLOR = "#f5a623";
export const CHUZI_NEBULA_CENTER: [number, number, number] = [0, 28, -48];
export const CHUZI_NEBULA_RADIUS = 14;
export const CHUZI_MINE_NEBULA_CENTER: [number, number, number] = [0, 38, -62];

/** Minimum gap between constellation bounds inside a compact platform nebula. */
const CHUZI_NEBULA_GAP_SCENES = 3;
const CHUZI_NEBULA_BOUNDARY_PAD = 8;
const TITLE_ARC_WORLD_SCALE = 58;
const TITLE_VISUAL_PADDING = 6;

/** World-space radius reserved for arched constellation title typography. */
export function estimateConstellationTitleFootprintRadius(
  title: string,
  sceneFootprint: number = COSMOS_DEFAULT_FOOTPRINT_RADIUS,
): number {
  const trimmed = title.trim();
  if (!trimmed) return sceneFootprint;

  const fontSize = 34;
  const letterSpacing = 6;
  const glyphWidth = fontSize * 0.62;
  const chars = trimmed.length;
  const pathWidthSvg =
    chars * glyphWidth + Math.max(0, chars - 1) * letterSpacing + 56;
  const titleRadius =
    pathWidthSvg / TITLE_ARC_WORLD_SCALE / 2 + TITLE_VISUAL_PADDING;

  return Math.max(sceneFootprint, titleRadius);
}

export type ChuziNebulaLayout = {
  cluster: TagNebulaCluster;
  storyPositions: Map<string, [number, number, number]>;
};

export type AllCosmosLayout = {
  chuziCluster: TagNebulaCluster;
  tagLayout: TagNebulaLayout;
  allStories: StoryListItem[];
  storyPositions: Map<string, [number, number, number]>;
};

function resolveStoryFootprint(
  story: StoryListItem,
  footprintByStoryId?: Map<string, number>,
): number {
  const sceneFootprint =
    footprintByStoryId?.get(story.id) ?? COSMOS_DEFAULT_FOOTPRINT_RADIUS;
  return estimateConstellationTitleFootprintRadius(story.title, sceneFootprint);
}

function placeStoriesAroundCenter(
  stories: StoryListItem[],
  center: [number, number, number],
  footprintByStoryId?: Map<string, number>,
): { storyPositions: Map<string, [number, number, number]>; nebulaRadius: number } {
  if (stories.length === 0) {
    return { storyPositions: new Map(), nebulaRadius: CHUZI_NEBULA_RADIUS };
  }

  const inputs = stories
    .map((story) => ({
      id: story.id,
      footprintRadius: resolveStoryFootprint(story, footprintByStoryId),
    }))
    .sort((a, b) => b.footprintRadius - a.footprintRadius);

  const anchors = distributeConstellationAnchors(inputs, {
    minGapScenes: CHUZI_NEBULA_GAP_SCENES,
  });

  let maxExtent = 0;
  const storyPositions = new Map<string, [number, number, number]>();
  for (const story of stories) {
    const local = anchors.get(story.id) ?? [0, 0, 0];
    const footprint = resolveStoryFootprint(story, footprintByStoryId);
    maxExtent = Math.max(maxExtent, Math.hypot(local[0], local[2]) + footprint);
    storyPositions.set(story.id, [
      center[0] + local[0],
      center[1] + local[1],
      center[2] + local[2],
    ]);
  }

  const nebulaRadius = Math.max(
    CHUZI_NEBULA_RADIUS,
    maxExtent + CHUZI_NEBULA_BOUNDARY_PAD,
  );

  return { storyPositions, nebulaRadius };
}

export function buildChuziNebulaLayout(
  systemStories: StoryListItem[],
  options?: {
    center?: [number, number, number];
    footprintByStoryId?: Map<string, number>;
  },
): ChuziNebulaLayout {
  const center = options?.center ?? CHUZI_NEBULA_CENTER;
  const { storyPositions, nebulaRadius } = placeStoriesAroundCenter(
    systemStories,
    center,
    options?.footprintByStoryId,
  );

  return {
    cluster: {
      tag: CHUZI_CLUSTER_TAG,
      center,
      radius: nebulaRadius,
      storyIds: systemStories.map((story) => story.id),
    },
    storyPositions,
  };
}

export function buildAllCosmosLayout(
  systemStories: StoryListItem[],
  userStories: StoryListItem[],
  footprintByStoryId?: Map<string, number>,
): AllCosmosLayout {
  const chuzi = buildChuziNebulaLayout(systemStories, { footprintByStoryId });
  const tagLayout = buildTagNebulaLayout(userStories, footprintByStoryId);
  const storyPositions = new Map<string, [number, number, number]>([
    ...chuzi.storyPositions,
    ...tagLayout.storyPositions,
  ]);

  return {
    chuziCluster: chuzi.cluster,
    tagLayout,
    allStories: [...systemStories, ...userStories],
    storyPositions,
  };
}

export function buildAllNebulaClusters(
  chuziCluster: TagNebulaCluster,
  tagClusters: TagNebulaCluster[],
): TagNebulaCluster[] {
  return [chuziCluster, ...tagClusters];
}
